import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import GlobalStyles from '@mui/material/GlobalStyles';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { Feature, FeatureCollection, Position } from 'geojson';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, NavigationControl, Popup, Source } from 'react-map-gl/mapbox';
import type { MapEvent, MapMouseEvent, MapRef } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

import { MAP_BASE_STYLE, MAPBOX_TOKEN } from '../../../../config/mapbox';
import {
  formatCompact,
  formatSnapRate,
  SNAP_SUPPRESSED_NOTE,
  type AreaMetrics,
} from '../../data/impactTypes';
import { getAreaMetrics } from '../../services/impactService';
import type { GeoLevel } from '../../types';

/** Public datasets — no tileset infra needed. */
const US_STATES_GEOJSON_URL =
  'https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json';
const US_COUNTIES_GEOJSON_URL =
  'https://raw.githubusercontent.com/plotly/datasets/master/geojson-counties-fips.json';

const STATE_FILL = 'state-fill';
const COUNTY_FILL = 'county-fill';
const US_BOUNDS: [[number, number], [number, number]] = [
  [-128, 22],
  [-65, 50],
];

export interface ToggledArea {
  fips: string;
  name: string;
  level: GeoLevel;
}

interface ImpactMapProps {
  geoLevel: GeoLevel;
  /** FIPS selected at the active level (state or county). */
  selectedFips: string[];
  /** State FIPS whose counties are shown in county view (empty = all US). */
  countyScope: string[];
  /** State FIPS the map should zoom to fit (empty = whole US). */
  fitToStateFips: string[];
  onToggleArea: (area: ToggledArea) => void;
  /**
   * Mobile has no left Overview Panel and no top-right Projections overlay
   * to dodge — instead a bottom sheet covers part of the map — so the
   * fit-bounds padding/offset (tuned for desktop's asymmetric layout) needs
   * its own mobile values, or the selection renders pushed off-center.
   */
  isMobile?: boolean;
}

interface HoverInfo {
  longitude: number;
  latitude: number;
  metrics: AreaMetrics;
}

/** Copy each feature's id into `properties.fips` so paint + hit-testing can
 *  key on it uniformly across states + counties. */
function withFips(fc: FeatureCollection): FeatureCollection {
  return {
    ...fc,
    features: fc.features.map((f: Feature) => ({
      ...f,
      properties: { ...f.properties, fips: String(f.id ?? '') },
    })),
  };
}

/** Bounding box of the given state FIPS within the states GeoJSON. */
function statesBbox(
  fc: FeatureCollection,
  fipsList: string[],
): [[number, number], [number, number]] | null {
  if (!fipsList.length) return null;
  const wanted = new Set(fipsList);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let found = false;

  const walk = (coords: Position | Position[] | Position[][] | Position[][][]) => {
    if (typeof (coords as Position)[0] === 'number') {
      const [x, y] = coords as Position;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      return;
    }
    (coords as Position[]).forEach((c) => walk(c as never));
  };

  for (const f of fc.features) {
    if (!wanted.has(String(f.id))) continue;
    if (f.geometry.type !== 'Polygon' && f.geometry.type !== 'MultiPolygon') continue;
    found = true;
    walk(f.geometry.coordinates as never);
  }
  return found
    ? [
        [minX, minY],
        [maxX, maxY],
      ]
    : null;
}

/** Fetch + normalise a GeoJSON url once. */
function useGeoJson(url: string | null) {
  const [data, setData] = useState<FeatureCollection | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`GeoJSON ${res.status}`);
        return res.json();
      })
      .then((json: FeatureCollection) => !cancelled && setData(withFips(json)))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [url]);

  return { data, error };
}

/** Feature → { fips, name } for the active level. */
function readFeature(feature: Feature, level: GeoLevel): { fips: string; name: string } {
  const props = feature.properties ?? {};
  const fips = String(props.fips ?? feature.id ?? '');
  const name =
    level === 'county'
      ? `${(props.NAME as string) ?? 'County'} County`
      : ((props.name as string) ?? 'State');
  return { fips, name };
}

export function ImpactMap({
  geoLevel,
  selectedFips,
  countyScope,
  fitToStateFips,
  onToggleArea,
  isMobile = false,
}: ImpactMapProps) {
  const theme = useTheme();
  const isCounty = geoLevel === 'county';
  const mapRef = useRef<MapRef | null>(null);

  const states = useGeoJson(US_STATES_GEOJSON_URL);
  // Counties are large (~3MB) — only fetch once the county view is opened.
  const [wantCounties, setWantCounties] = useState(false);
  useEffect(() => {
    if (isCounty) setWantCounties(true);
  }, [isCounty]);
  const counties = useGeoJson(wantCounties ? US_COUNTIES_GEOJSON_URL : null);

  const [hover, setHover] = useState<HoverInfo | null>(null);
  const [hoverFips, setHoverFips] = useState<string>('');
  const [mapLoaded, setMapLoaded] = useState(false);

  const selectedFill = theme.palette.primary.main;
  const landFill = theme.palette.surface.mapLand;
  const outline = theme.palette.primary.main;
  const activeFillId = isCounty ? COUNTY_FILL : STATE_FILL;

  // County view: only the counties within the scoped states (empty = all US).
  const countyView = useMemo(() => {
    if (!counties.data) return null;
    if (!countyScope.length) return counties.data;
    const scope = new Set(countyScope);
    return {
      ...counties.data,
      features: counties.data.features.filter((f) =>
        scope.has(String(f.id ?? '').slice(0, 2)),
      ),
    } as FeatureCollection;
  }, [counties.data, countyScope]);

  const activeData = isCounty ? countyView : states.data;
  const activeError = isCounty ? counties.error : states.error;
  const activeLoading = !activeData && !activeError;

  // Selected areas fill royal blue; the rest are opaque white land so county
  // borders read as outlines. `match` keys on `fips` for both levels.
  const fillPaint = useMemo(() => {
    const list = selectedFips.length ? selectedFips : ['__none__'];
    return {
      'fill-color': ['match', ['get', 'fips'], list, selectedFill, landFill] as unknown as string,
      'fill-opacity': ['match', ['get', 'fips'], list, 0.9, 1] as unknown as number,
    };
  }, [selectedFips, selectedFill, landFill]);

  // Zoom to fit the target states whenever they change (or on first load).
  // Desktop reserves extra right/left padding (and nudges the fit off-center)
  // to dodge the left Overview Panel and top-right Projections overlay.
  // Mobile has neither — instead a bottom sheet covers the lower part of the
  // map — so it needs symmetric side padding, no offset, and extra bottom
  // clearance instead.
  const fitKey = fitToStateFips.join(',');
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !states.data || !mapLoaded) return;
    const bbox = statesBbox(states.data, fitToStateFips);
    if (bbox) {
      map.fitBounds(
        bbox,
        isMobile
          ? {
              padding: { top: 40, bottom: 130, left: 30, right: 30 },
              maxZoom: 6.5,
              duration: 800,
            }
          : {
              // Desktop's MapControls float on top of the map (absolute
              // overlay, ~60px tall from the map's top edge) — a shape
              // fitted with too little top clearance can end up right
              // behind them, and its hover popup (which extends downward
              // from the hovered point) then opens on top of the controls,
              // blocking clicks on the age/amount/location selects.
              padding: { top: 90, bottom: 40, left: 40, right: 220 },
              // Nudge the focused state a little down (+y) and left (-x).
              offset: [-40, 30],
              maxZoom: 6.5,
              duration: 800,
            },
      );
    } else {
      map.fitBounds(US_BOUNDS, {
        padding: isMobile
          ? { top: 40, bottom: 130, left: 20, right: 20 }
          : { top: 90, bottom: 60, left: 48, right: 200 },
        duration: 800,
      });
    }
    // fitKey captures the fips list; states.data + mapLoaded gate readiness.
  }, [fitKey, states.data, mapLoaded, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shared by hover (desktop) and tap (mobile) — resolves a feature to its
  // metrics and opens the popup there, or closes it if the area has no
  // live data yet (rather than showing fabricated numbers).
  const showHoverFor = useCallback(
    (feature: Feature, lngLat: { lng: number; lat: number }) => {
      const { fips } = readFeature(feature, geoLevel);
      setHoverFips(fips);
      const metrics = getAreaMetrics(geoLevel, fips);
      if (!metrics) {
        setHover(null);
        return;
      }
      setHover({ longitude: lngLat.lng, latitude: lngLat.lat, metrics });
    },
    [geoLevel],
  );

  const handleClick = useCallback(
    (event: MapMouseEvent) => {
      const feature = event.features?.[0];
      if (!feature) {
        // Tapping empty map (water, or outside the US) closes the popup —
        // there's no hover/mouseleave equivalent on touch to do this for us.
        if (isMobile) {
          setHover(null);
          setHoverFips('');
        }
        return;
      }
      const { fips, name } = readFeature(feature, geoLevel);
      if (fips) onToggleArea({ fips, name, level: geoLevel });
      // On mobile, the same tap that selects/deselects an area also opens
      // its info popup — there's no separate hover gesture to preview it
      // first, so tapping does both at once.
      if (isMobile) showHoverFor(feature, event.lngLat);
    },
    [geoLevel, onToggleArea, isMobile, showHoverFor],
  );

  const handleMouseMove = useCallback(
    (event: MapMouseEvent) => {
      event.target.getCanvas().style.cursor = 'pointer';
      const feature = event.features?.[0];
      if (!feature) {
        setHover(null);
        setHoverFips('');
        return;
      }
      showHoverFor(feature, event.lngLat);
    },
    [showHoverFor],
  );

  const handleMouseLeave = useCallback((event: MapMouseEvent) => {
    event.target.getCanvas().style.cursor = '';
    setHover(null);
    setHoverFips('');
  }, []);

  // Mobile has no mouseleave to close the popup when the user's attention
  // moves elsewhere (the bottom sheet, the controls row, etc.) — close it
  // on any tap outside the map itself. Taps inside the map are already
  // handled by handleClick above (they either move the popup to the newly
  // tapped area or close it if that spot has no feature/data).
  useEffect(() => {
    if (!isMobile || !hover) return undefined;
    const handlePointerDown = (event: PointerEvent) => {
      const container = mapRef.current?.getContainer();
      if (container && !container.contains(event.target as Node)) {
        setHover(null);
        setHoverFips('');
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isMobile, hover]);

  // Retint the Carto basemap to the Figma palette: pale-blue water and
  // light-blue state/county borders (Carto ships gray water + pink borders).
  const retintBasemap = useCallback(
    (event: MapEvent) => {
      const map = event.target;
      // Allow two-finger pinch-zoom on touch devices, but keep the map
      // north-up (no rotation).
      map.touchZoomRotate.disableRotation();
      // Gate the zoom-to-fit until the map is ready, so the initial fit isn't
      // clobbered by the map's own initialViewState during startup.
      setMapLoaded(true);
      const water = theme.palette.surface.mapWater;
      const borderSoft = alpha(theme.palette.primary.main, 0.2);
      const setPaint = map.setPaintProperty.bind(map) as (
        id: string,
        prop: string,
        value: string,
      ) => void;
      const set = (id: string, prop: string, value: string) => {
        if (map.getLayer(id)) setPaint(id, prop, value);
      };
      set('water', 'fill-color', water);
      set('waterway', 'line-color', water);
      set('boundary_country_inner', 'line-color', borderSoft);
      // We draw our own US state/county borders from the Census GeoJSON
      // (the `area-outline` layer below) — the basemap's own admin-level-4
      // boundary layer is both redundant for the US and, since it's global,
      // the thing drawing Canadian provinces / Mexican states. Hide it
      // rather than retint it.
      if (map.getLayer('boundary_state')) {
        map.setLayoutProperty('boundary_state', 'visibility', 'none');
      }
    },
    [theme],
  );

  if (states.error || activeError) {
    return (
      <Box sx={styles.stateBox}>
        <Typography color="text.secondary">Map data unavailable</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* One shadow, one place: overrides mapbox's own default popup shadow
          rather than stacking a second one on the inner card (two
          overlapping shadows at the same sharp edge read as an unwanted
          border). The doubled class beats mapbox's own
          `.mapboxgl-popup-anchor-* .mapboxgl-popup-content` rules, which
          zero out the corners on the anchored side. */}
      <GlobalStyles
        styles={(t: Theme) => ({
          '.impact-hover-popup.mapboxgl-popup .mapboxgl-popup-content': {
            borderRadius: t.radius('md'),
            boxShadow: t.elevations.subtle,
          },
          // The card floats on its own in the design — no pointer tip.
          '.impact-hover-popup .mapboxgl-popup-tip': { display: 'none' },
          // On mobile, MobileBottomSheet overlays the lower part of the map
          // at zIndex 3 — without this the popup for a tapped area (which
          // often lands near the bottom of the visible map) renders behind
          // the sheet and never appears at all.
          '.impact-hover-popup.mapboxgl-popup': { zIndex: 4 },
          // Mobile-only close button (desktop relies on hover/mouseleave) —
          // restyled from Mapbox's default gray square to match the card.
          '.impact-hover-popup .mapboxgl-popup-close-button': {
            top: t.spacing(1),
            right: t.spacing(1),
            width: 24,
            height: 24,
            fontSize: '1.125rem',
            lineHeight: 1,
            color: t.palette.text.secondary,
            borderRadius: '999px',
          },
          '.impact-hover-popup .mapboxgl-popup-close-button:hover': {
            color: t.palette.text.primary,
            backgroundColor: t.palette.divider,
          },
        })}
      />
      <Map
        ref={mapRef}
        initialViewState={{
          bounds: US_BOUNDS,
          fitBoundsOptions: {
            padding: isMobile
              ? { top: 40, bottom: 130, left: 20, right: 20 }
              : { top: 90, bottom: 60, left: 48, right: 200 },
          },
        }}
        mapStyle={MAP_BASE_STYLE}
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ position: 'absolute', inset: 0 }}
        interactiveLayerIds={activeData ? [activeFillId] : []}
        onLoad={retintBasemap}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        dragRotate={false}
        doubleClickZoom={false}
        pitchWithRotate={false}
      >
        <NavigationControl position="bottom-right" showCompass={false} />

        {activeData && (
          <Source
            key={isCounty ? 'counties' : 'states'}
            id={isCounty ? 'us-counties' : 'us-states'}
            type="geojson"
            data={activeData}
          >
            <Layer id={activeFillId} type="fill" paint={fillPaint} />
            <Layer
              id="area-outline"
              type="line"
              paint={{
                'line-color': outline,
                'line-width': isCounty ? 0.5 : 0.7,
                'line-opacity': isCounty ? 0.4 : 0.5,
              }}
            />
            <Layer
              id="area-hover-outline"
              type="line"
              filter={['==', ['get', 'fips'], hoverFips]}
              paint={{ 'line-color': theme.palette.primary.main, 'line-width': 2 }}
            />
          </Source>
        )}

        {hover && (
          <Popup
            longitude={hover.longitude}
            latitude={hover.latitude}
            closeButton={isMobile}
            closeOnClick={false}
            onClose={() => {
              setHover(null);
              setHoverFips('');
            }}
            anchor="top"
            offset={12}
            maxWidth="none"
            className="impact-hover-popup"
          >
            <HoverCard metrics={hover.metrics} level={geoLevel} />
          </Popup>
        )}
      </Map>

      {activeLoading && (
        <Box sx={styles.loadingBadge}>
          <CircularProgress size={18} />
          <Typography sx={styles.loadingText}>
            {isCounty ? 'Loading counties…' : 'Loading map…'}
          </Typography>
        </Box>
      )}
    </>
  );
}

function HoverCard({ metrics, level }: { metrics: AreaMetrics; level: GeoLevel }) {
  return (
    <Box sx={styles.card}>
      <Typography sx={styles.cardName}>{metrics.name}</Typography>
      <Typography sx={styles.cardType}>{level === 'county' ? 'County' : 'State'}</Typography>
      <Box sx={styles.scoreRow}>
        <Typography component="span" sx={styles.scoreValue}>
          {metrics.opportunityScore}
        </Typography>
        <Typography component="span" sx={styles.scoreLabel}>
          / 100 opportunity score
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={metrics.opportunityScore}
        sx={styles.scoreBar}
      />
      <Box sx={styles.grid}>
        <Stat label="Median income" value={formatCompact(metrics.medianIncome, true)} />
        <Stat label="Eligible under 18" value={formatCompact(metrics.eligibleUnder18)} />
        <Stat
          label="Receiving SNAP"
          value={formatSnapRate(metrics.receivingSNAP)}
          note={metrics.receivingSNAP > 100 ? SNAP_SUPPRESSED_NOTE : undefined}
        />
        <Stat label="529 participation" value={`${metrics.participation529}%`} />
      </Box>
    </Box>
  );
}

function Stat({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  return (
    <Box>
      <Typography sx={styles.statLabel}>{label}</Typography>
      <Typography sx={styles.statValue}>{value}</Typography>
      {note && <Typography sx={styles.statNote}>{note}</Typography>}
    </Box>
  );
}

const styles = {
  stateBox: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingBadge: {
    position: 'absolute',
    bottom: 16,
    left: 16,
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderRadius: 999,
    paddingInline: 1.5,
    paddingBlock: 0.75,
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
  },
  loadingText: { fontSize: '0.8125rem', color: 'text.secondary' },
  // No background/shadow here — the mapboxgl-popup-content wrapper this
  // renders inside already provides both (see the GlobalStyles override
  // above); adding a second one here double-stacks shadows into a hard edge.
  card: { minWidth: 230, p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 },
  cardName: { fontSize: '1.0625rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.2 },
  cardType: { fontSize: '0.8125rem', color: 'text.secondary', mb: 0.5 },
  scoreRow: { display: 'flex', alignItems: 'baseline', gap: 0.5 },
  scoreValue: { fontSize: '1.5rem', fontWeight: 800, color: 'text.primary', lineHeight: 1 },
  scoreLabel: { fontSize: '0.75rem', color: 'text.secondary' },
  // Matches the selected-area cards in the Overview Panel.
  scoreBar: {
    height: 6,
    borderRadius: 999,
    backgroundColor: 'divider',
    '& .MuiLinearProgress-bar': { borderRadius: 999, backgroundColor: 'primary.main' },
  },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 0.5 },
  statLabel: { fontSize: '0.75rem', color: 'text.secondary' },
  statValue: { fontSize: '0.9375rem', fontWeight: 700, color: 'primary.main' },
  statNote: { fontSize: '0.6875rem', color: 'text.secondary' },
} as const;
